import TableLayout from "@/components/layouts/page/TableLayout";
import {Filters} from "@/components/layouts/page/TableLayout/Filters";

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {
  getConfirmations,
  getSearchType,
  setConfirmationsDate,
  setConfirmationsSearchType,
  setPage,
  setSort,
} from "@redux/slices/dashboardSlices/dashboardCertificationSlice";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import moment from "moment";
import {Doughnut} from "react-chartjs-2";
import CountUp from "react-countup";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {Head} from "../../components/Head";
import styles from "./styles.module.scss";

ChartJS.register(
  ArcElement,
  Title,
  Filler,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
);

export const Confirmations = () => {
  const minDate = moment().subtract(10, "years").toDate();
  const [withArchived, setWithArchived] = useState(false);
  const searchRequestsRef = useRef({});
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {width, isMobile} = useSelector((state) => state.common.settings);

  const {
    date: confirmationsDate,
    data: confirmationsData,
    status,
    error,
    page,
    size,
    totalPages,
    totalSucceeded,
    totalFailed,
    totalInProgress,
    sort,
  } = useSelector((state) => state.dashboard.certification.confirmations);

  const searchType = useSelector(
    (state) => state.dashboard.certification.confirmations.searchType,
  );
  const selectedIds = useSelector(
    (state) =>
      state.dashboard.certification.confirmations.searchType.selectedIds,
  );

  const onSetDate = async (key, date) => {
    const isoValue = date instanceof Date ? date.toISOString() : date;
    const newDate = {...confirmationsDate, [key]: isoValue};

    await dispatch(setConfirmationsDate(newDate));
  };

  const chartCutout = (width * 3.25) / 100;
  const chartFontSize = (width * 0.75) / 100;
  const chartPadding = (width * 0.5) / 100;
  const chartBorder = (width * 0.25) / 100;
  const margin = (width * 0.5) / 100;

  const STATUSES_ORDER = [
    t("page.dashboard.certification_status.passed"),
    t("page.dashboard.certification_status.failed"),
    t("page.dashboard.certification_status.in_progress"),
  ];
  const STATUS_COLORS = {
    [t("page.dashboard.certification_status.passed")]: "#57D14C",
    [t("page.dashboard.certification_status.failed")]: "#F24E4E",
    [t("page.dashboard.certification_status.in_progress")]: "#F59F2E",
  };

  const handlePageChange = (selectedPage) => {
    dispatch(setPage(selectedPage + 1));
  };

  const buildDoughnutDataset = (
    succeeded,
    failed,
    inProgress,
    borderRadiusValue,
  ) => {
    const statusValues = [succeeded || 0, failed || 0, inProgress || 0];
    const statusToCount = STATUSES_ORDER.reduce((acc, status, index) => {
      acc[status] = statusValues[index];
      return acc;
    }, {});

    const labels = STATUSES_ORDER.filter((s) => statusToCount[s] > 0);
    const values = labels.map((label) => statusToCount[label] || 0);
    const colors = labels.map((label) => STATUS_COLORS[label]);
    const total = values.reduce((a, b) => a + b, 0);

    return {
      labels,
      total,
      chartData: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderRadius: borderRadiusValue,
            borderWidth: 0,
            hoverOffset: 0,
            offset: [0, 0],
          },
        ],
      },
    };
  };

  const formatTooltipPercent = (ctx) => {
    const ds = ctx.dataset;
    const total = (ds.data || []).reduce((a, b) => a + b, 0) || 1;
    const value = Number(ctx.parsed || 0);
    const pct = Math.round((value / total) * 100);

    return `${ctx.label}: ${pct}%`;
  };

  const handleSelectSort = (option) => {
    dispatch(setSort(option));
    dispatch(setPage(1));
  };

  const runSearch = useCallback(
    (type, name = "") => {
      if (searchRequestsRef.current[type]?.abort) {
        searchRequestsRef.current[type].abort();
      }
      const request = dispatch(
        getSearchType({
          type,
          name,
          section: "confirmations",
          archived: withArchived,
        }),
      );
      searchRequestsRef.current[type] = request;
      return request;
    },
    [dispatch, withArchived],
  );

  const handleSearch = useCallback(
    (type, searchValue) => {
      runSearch(type, searchValue?.trim() || "");
    },
    [runSearch],
  );

  useEffect(() => {
    runSearch("certification");
    runSearch("manager");
    runSearch("product_line");

    return () => {
      Object.values(searchRequestsRef.current).forEach((request) => {
        if (request?.abort) request.abort();
      });
    };
  }, [runSearch]);

  const filtersList = useMemo(
    () => [
      {
        id: "certification",
        placeholder: t("page.dashboard.certification_placeholder"),
        searchPlaceholder: t("page.dashboard.certification_search"),
        resetOptionLabel: t("input.not_selected"),
        activeOption:
          searchType.certification?.find(
            (opt) => opt.id === selectedIds?.certification,
          )?.name || "",
        options: searchType.certification,
        onSelect: (option) => {
          dispatch(
            setConfirmationsSearchType({
              type: "certification",
              id: option === null ? null : option.id,
            }),
          );
        },
        onSearch: (searchValue) => handleSearch("certification", searchValue),
        emptyResult: null,
        multiSelect: false,
      },
      {
        id: "manager",
        placeholder: t("page.dashboard.manager_placeholder"),
        searchPlaceholder: t("page.dashboard.manager_search"),
        resetOptionLabel: t("input.not_selected"),
        activeOption:
          searchType.manager?.find((opt) => opt.id === selectedIds?.manager)
            ?.name || "",
        options: searchType.manager || [],
        onSelect: (option) => {
          dispatch(
            setConfirmationsSearchType({
              type: "manager",
              id: option === null ? null : option.id,
            }),
          );
        },
        onSearch: (searchValue) => handleSearch("manager", searchValue),
        emptyResult: null,
        multiSelect: false,
      },
      {
        id: "product_line",
        placeholder: t("page.dashboard.product_line_placeholder"),
        searchPlaceholder: t("page.dashboard.product_line_search"),
        resetOptionLabel: t("input.not_selected"),
        activeOption:
          searchType.product_line?.find(
            (opt) => opt.id === selectedIds?.product_line,
          )?.name || "",
        options: searchType.product_line || [],
        onSelect: (option) => {
          dispatch(
            setConfirmationsSearchType({
              type: "product_line",
              id: option === null ? null : option.id,
            }),
          );
        },
        onSearch: (searchValue) => handleSearch("product_line", searchValue),
        emptyResult: null,
        multiSelect: false,
      },
    ],
    [t, searchType, selectedIds, dispatch, handleSearch],
  );

  const columns = [
    {
      Header: t("table.certification_number"),
      accessor: "id",
      Cell: ({row}) => {
        const rowNumber = (page - 1) * size + row.index + 1;
        return <>{rowNumber}</>;
      },
      width: 150,
    },
    {
      Header: t("table.user"),
      accessor: "name",
      Cell: ({value, row}) => (
        <>
          {value} {row.original.lastname}
        </>
      ),
      width: 300,
    },
    {
      Header: t("table.scenario"),
      accessor: "scenario_name",
      width: 290,
    },
    {
      Header: t("table.result"),
      accessor: "feedback_score",
      Cell: ({cell: _, row}) =>
        `${row?.original?.status === "succeeded" ? t("table.certified") : t("table.not_certified")} / ${Math.round(Number(row?.original?.feedback_score)) || 0}`,
      width: 230,
    },
    {
      Header: "",
      accessor: "emptyEnd",
      width: 0,
    },
  ];

  const conductedTemplate = t("page.dashboard.confirmed", {count: "___"});
  const [beforeCount, afterCount] = conductedTemplate.split("___");

  const {total: doughnutTotal, chartData: doughnutData} = buildDoughnutDataset(
    totalSucceeded ?? 0,
    totalFailed ?? 0,
    totalInProgress ?? 0,
    margin,
  );

  const doughnutOptions = {
    cutout: width >= 1920 || isMobile ? 80 : chartCutout,
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 3000,
    },
    plugins: {
      legend: {
        position: isMobile ? "bottom" : "right",
        labels: {
          boxWidth: width >= 1920 || isMobile ? 20 : chartFontSize,
          boxHeight: width >= 1920 || isMobile ? 20 : chartFontSize,
          padding: width >= 1920 || isMobile ? 16 : chartFontSize,
          usePointStyle: true,
          color: "#414040",
          font: {
            size: width >= 1920 || isMobile ? 14 : chartFontSize,
            family: "'IBM Plex Sans', sans-serif",
          },
        },
      },
      tooltip: {
        backgroundColor: "white",
        titleColor: "#414040",
        bodyColor: "#414040",
        usePointStyles: true,
        borderColor: "rgba(247, 207, 221, 0.80)",
        padding: width >= 1920 || isMobile ? 20 : chartPadding,
        caretPadding: width >= 1920 || isMobile ? 20 : chartPadding,
        cornerRadius: width >= 1920 || isMobile ? 20 : chartPadding,
        boxPadding: width >= 1920 || isMobile ? 20 : chartPadding,
        usePointStyle: true,
        borderWidth: width >= 1920 || isMobile ? 2 : chartBorder - 1.5,
        titleAlign: "center",
        bodyAlign: "right",
        titleFont: {
          size: width >= 1920 || isMobile ? 16 : chartFontSize,
          family: "'IBM Plex Sans', sans-serif",
        },
        bodyFont: {
          size: width >= 1920 || isMobile ? 14 : chartFontSize,
          family: "'IBM Plex Sans', sans-serif",
        },
        callbacks: {
          label: (ctx) => formatTooltipPercent(ctx),
        },
      },
    },
  };

  useEffect(() => {
    dispatch(
      getConfirmations({
        scenarioId: selectedIds?.certification,
        managerId: selectedIds?.manager,
        productLineId: selectedIds?.product_line,
        fromDate: confirmationsDate.from,
        toDate: confirmationsDate.to,
        page,
        pageSize: size,
        withArchived,
        sort,
      }),
    );
  }, [
    confirmationsDate.from,
    confirmationsDate.to,
    withArchived,
    selectedIds?.certification,
    selectedIds?.manager,
    selectedIds?.product_line,
    page,
    size,
    dispatch,
    sort,
  ]);

  return (
    <div className={styles.confirmations}>
      <Head
        title={t("page.dashboard.confirmations_title")}
        checkboxLabel={t("page.dashboard.checkbox_filter")}
        setWithArchived={setWithArchived}
        withArchived={withArchived}
        chartsDate={confirmationsDate}
        minDate={minDate}
        setFromDate={(date) => onSetDate("from", date)}
        setToDate={(date) => onSetDate("to", date)}
        maxWidth={"100%"}
      />

      <Filters filtersList={filtersList} />

      <div className={styles.confirmationsChartsWrapper}>
        <div className={styles.confirmationsUsersTable}>
          <TableLayout
            pagination
            settings={false}
            searchPlaceholder={t("table.search_users")}
            serverStatus={status}
            serverError={error}
            dataSort={sort}
            selectSort={handleSelectSort}
            columns={columns}
            data={confirmationsData}
            dataSize={size}
            totalPages={totalPages}
            currentPage={page}
            toPage={handlePageChange}
            minHeight={440}
          />
        </div>

        <div className={styles.confirmationsDoughnutWrapper}>
          <div className={styles.confirmationsDoughnutTitle}>
            <RootDesc>
              <span>
                {beforeCount}
                <strong>
                  <CountUp
                    end={doughnutTotal}
                    duration={3}
                    preserveValue
                  />
                </strong>
                {afterCount}
              </span>
            </RootDesc>
          </div>

          <div className={styles.confirmationsDoughnut}>
            <Doughnut
              key={`${totalSucceeded}-${totalFailed}-${totalInProgress}`}
              data={doughnutData}
              options={doughnutOptions}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
